// Field help shared by several screens: the filter bar's month range, customer, model and cost
// basis, and the markup / margin pair. Keys are the field ids passed to <Field help="…">.
import type { HelpTopic } from "../topic";

export const COMMON_TOPICS = {
  "common.months": {
    title: "Months",
    body: "The calendar months (UTC) the figures cover, from the first month to the last, inclusive. Open the button to pick the first month, then the last.",
    example: "Mar 2026 – Aug 2026 covers six months: March 1 00:00 UTC to the end of August 31.",
    article: "revenue-and-margin",
  },
  "common.month": {
    title: "Month",
    body: "One calendar month in UTC. Nexus bills, closes and reports by UTC month.",
    example: "August 2026 runs from Aug 1 00:00 UTC to Aug 31 23:59:59 UTC, whatever your own time zone.",
    article: "closing-a-month",
  },
  "common.customer": {
    title: "Customer",
    body: "Which customer to show. Type part of the name (or the id) and choose from the list; leave it empty for every customer where the screen allows it.",
    example: "Typing “hoo” finds Hooli (id hooli). The id is shown under the name so two similarly named customers can be told apart.",
    article: "adding-a-customer",
  },
  "common.model": {
    title: "Model",
    body: "Limit the figures to one Bedrock model, by its model id. Leave it empty for all models.",
    example: "anthropic.claude-sonnet-4-5 shows only Claude Sonnet 4.5 usage; other models are left out of every total.",
    article: "model-prices",
  },
  "common.costBasis": {
    title: "Cost basis",
    body: "Which AWS cost profit is measured against. AWS list price prices every token at the published Bedrock rate. Actual AWS bill uses what AWS really charged, from the Cost and Usage Report, including discounts and credits.",
    example: "A customer used $1,000 of tokens at list price. With a 10 % private discount the actual AWS bill is $900, so profit looks $100 higher on the Actual AWS bill basis.",
    article: "aws-costs",
    section: "expense-basis",
  },
  "common.markupAndMargin": {
    title: "Markup and margin",
    body: "Markup is what is added on top of AWS cost. Margin is the share of the bill that is kept. They describe the same profit from two sides.",
    example: "AWS cost $1,000, billed $1,300, profit $300. Markup = 300 ÷ 1,000 = 30 %. Margin = 300 ÷ 1,300 = 23.1 %.",
    article: "pricing-and-markup",
    section: "markup-is-not-margin",
  },
  "common.grossProfit": {
    title: "Gross profit",
    body: "What customers were billed minus what their Bedrock usage cost AWS, on the chosen cost basis. Platform running costs are not subtracted.",
    example: "Billed $124,137.54 and AWS cost $95,490.41 give a gross profit of $28,647.13.",
    article: "revenue-and-margin",
  },
  "common.awsCost": {
    title: "AWS cost",
    body: "What the usage cost on the chosen basis: tokens at AWS list price, or the actual AWS bill from the Cost and Usage Report.",
    example: "1 million Claude input tokens at a $3.00 list price cost $3.00 on the list-price basis.",
    article: "aws-costs",
  },
  "common.billed": {
    title: "Billed",
    body: "What the customer is invoiced for the usage: AWS cost with the rate plan's markup, plus approved adjustments.",
    example: "$1,000 of AWS cost on a 30 % markup plan bills $1,300.",
    article: "pricing-and-markup",
    section: "tokens-to-invoice",
  },
  "common.status": {
    title: "Status",
    body: "Where the item stands. The chip's words say it plainly: for example Active, Draft, Open or Closed.",
    example: "A month shows Open while usage can still arrive, and Closed once its invoice is fixed.",
    article: "glossary",
  },
  "common.search": {
    title: "Search",
    body: "Filters the list as you type. It matches names and ids.",
    example: "“acme” finds Acme Rocket Skates and any id containing acme.",
    article: "glossary",
  },
} satisfies Record<string, HelpTopic>;
